import { Box, Button, CircularProgress, Fade, Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { grey } from "../../../../../../theme/muiColors";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSwapy } from "swapy";
import { setContractors, setEmployees, setStaffErrors, setSupervisors } from "../../../../../../redux/features/jobCreateSlice";
import { gateway } from "../../../../../../gateway";
import { IRoles } from "../../../../../../utils";
import JobEmployeeAccept from "./JobEmployeeAccept";
import { SwapyContainerTeam } from "./SwapyContainerTeam";
import { SwapyTeamSection } from "./SwapyTeamSlots";
import { colors, alphaVar } from "@components/ui/colors";
import { Banner } from "@components/Banner";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { useUpdateJobEventWorkforceAction, useUpdateJobDefaultWorkforceAction } from "@components/jobs/jobs-list/hooks/useUpdate";
import { useDialog } from "@components/ui/dialogs/useDialog";
import { useQueryClient } from "@tanstack/react-query";

function a11yProps(index) {
  return {
    id: `job-tab-${index}`,
    "aria-controls": `job-tabpanel-${index}`,
  };
}

const MAX_SLOTS_SUPERVISOR = 3;
const MAX_SLOTS_EMPLOYEE = 3;
const MAX_SLOTS_CONTRACTOR = 3;

const validateSwapyStructure = (root) => {
  if (!root) return false;

  const slots = Array.from(root.querySelectorAll("[data-swapy-slot]"));
  const items = Array.from(root.querySelectorAll("[data-swapy-item]"));

  const slotNames = new Map();
  const itemNames = new Map();

  let isValid = true;

  slots.forEach((slot) => {
    const name = slot.getAttribute("data-swapy-slot");

    if (!name) {
      console.error("Swapy invalid: slot without name", slot);
      isValid = false;
      return;
    }

    if (slotNames.has(name)) {
      console.error("Swapy invalid: duplicated slot", name, {
        first: slotNames.get(name),
        second: slot,
      });
      isValid = false;
    }

    slotNames.set(name, slot);
  });

  items.forEach((item) => {
    const name = item.getAttribute("data-swapy-item");

    if (!name) {
      console.error("Swapy invalid: item without name", item);
      isValid = false;
      return;
    }

    if (itemNames.has(name)) {
      console.error("Swapy invalid: duplicated item", name, {
        first: itemNames.get(name),
        second: item,
      });
      isValid = false;
    }

    itemNames.set(name, item);

    const parentSlot = item.closest("[data-swapy-slot]");

    if (!parentSlot || !root.contains(parentSlot)) {
      console.error("Swapy invalid: item outside slot", name, item);
      isValid = false;
    }
  });

  slots.forEach((slot) => {
    const directItems = Array.from(slot.children).filter((child) => child.hasAttribute("data-swapy-item"));

    const allItemsInside = Array.from(slot.querySelectorAll("[data-swapy-item]"));

    if (allItemsInside.length > 1) {
      console.error("Swapy invalid: slot has more than one item", {
        slot: slot.getAttribute("data-swapy-slot"),
        items: allItemsInside.map((i) => i.getAttribute("data-swapy-item")),
        element: slot,
      });
      isValid = false;
    }

    if (allItemsInside.length === 1 && directItems.length === 0) {
      console.error("Swapy invalid: item is not direct child of slot", {
        slot: slot.getAttribute("data-swapy-slot"),
        item: allItemsInside[0].getAttribute("data-swapy-item"),
        element: slot,
      });
      isValid = false;
    }
  });

  return isValid;
};

const normalizeWorkforceType = (type) => {
  if (type === "supervisor") return "supervisors";
  if (type === "employee") return "employees";
  if (type === "contractor") return "contractors";

  return type;
};

export function JobApproved({
  data: job = null,
  currentWf = [],
  jobEventUid = null,
  jobId = null,
  mode = "create",
  saveMode = "event",
  onSaveWorkforce = null,
  onWorkforceChange = null,
  availabilityWindow = null,
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const updateJobEventWorkforceMutation = useUpdateJobEventWorkforceAction();
  const updateJobDefaultWorkforceMutation = useUpdateJobDefaultWorkforceAction();
  const { userRole } = useSelector((state) => state.jobSummaryState);
  const eventScheduler = useSelector((state) => state.jobCreateStepper?.eventScheduler);
  const scheduleWindow = useMemo(() => {
    if (availabilityWindow?.startAt && availabilityWindow?.endAt) return availabilityWindow;
    if (mode !== "create") return null;

    const config = eventScheduler?.eventPreferences?.eventConfig || eventScheduler?.eventConfig || null;
    if (!config || config.mode !== "SINGLE_EXECUTION" || !config.startDate || !config.duration) return null;

    const start = new Date(config.startDate);
    const minutes = Number(config.duration);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(minutes) || minutes <= 0) return null;

    return {
      startAt: start.toISOString(),
      endAt: new Date(start.getTime() + minutes * 60000).toISOString(),
    };
  }, [availabilityWindow, eventScheduler, mode]);
  const slotsRef = useRef(null);
  const swapyRef = useRef(null);
  const lastSlotMapRef = useRef({});
  const swapyInitializedRef = useRef(false);
  const initializedFromCurrentWfRef = useRef(false);
  const selectedWorkforceRef = useRef({
    supervisors: [],
    employees: [],
    contractors: [],
  });
  const assignmentVersionRef = useRef(job?.updatedAt);
  const selectionRevisionRef = useRef(0);
  if (!assignmentVersionRef.current && job?.updatedAt) assignmentVersionRef.current = job.updatedAt;

  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [supervisor, setSupervisor] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [contractor, setContractor] = useState([]);
  const [itemsWithError, setItemsWithError] = useState([]);
  const [itemsWithSuccess, setItemsWithSuccess] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [touchedItems, setTouchedItems] = useState([]);
  const [showStaffErrorBanner, setShowStaffErrorBanner] = useState(false);

  const [selectedWorkforce, setSelectedWorkforce] = useState({
    supervisors: [],
    employees: [],
    contractors: [],
  });

  const { showSnackbar } = useGlobalSnackbar();
  const { showDialog, DialogComponent } = useDialog();

  useEffect(() => {
    if (itemsWithError.length === 0) {
      setShowStaffErrorBanner(false);
      return;
    }

    setShowStaffErrorBanner(true);

    const timer = setTimeout(() => {
      setShowStaffErrorBanner(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, [itemsWithError.length]);

  const [swapyRenderKey, setSwapyRenderKey] = useState(0);

  const handleGetWorkforce = async (window) => {
    setLoading(true);

    try {
      const token = sessionStorage.getItem("token");

      const res = await gateway.get("/workforce/self", {
        headers: {
          Authorization: token,
        },
      });

      let workforceList = res.data?.data || [];

      if (window?.startAt && window?.endAt) {
        try {
          const unavailable = await gateway.get("/workforce/unavailable", {
            headers: { Authorization: token },
            params: {
              startAt: window.startAt,
              endAt: window.endAt,
              ...(window.excludeVisitUid ? { excludeVisitUid: window.excludeVisitUid } : {}),
            },
          });
          const blocked = new Set(unavailable.data?.data || []);
          workforceList = workforceList.filter((worker) => !blocked.has(worker.uid));
        } catch (error) {
          console.error("Unable to filter workforce by schedule:", error);
        }
      }

      setEmployee(workforceList.filter((worker) => worker.workforceRole === "employee"));

      setContractor(workforceList.filter((worker) => worker.workforceRole === "contractor"));

      setSupervisor(workforceList.filter((worker) => worker.workforceRole === "supervisor"));
    } catch (error) {
      console.error("Error loading workforce:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetWorkforce(scheduleWindow);

    return () => {
      if (swapyRef.current) {
        swapyRef.current.destroy();
        swapyRef.current = null;
      }

      swapyInitializedRef.current = false;
    };
  }, [scheduleWindow?.startAt, scheduleWindow?.endAt, scheduleWindow?.excludeVisitUid]);
  useEffect(() => {
    onWorkforceChange?.(selectedWorkforce);
    if (mode !== "create") return;
    dispatch(setSupervisors(selectedWorkforce.supervisors));
    dispatch(setEmployees(selectedWorkforce.employees));
    dispatch(setContractors(selectedWorkforce.contractors));
  }, [selectedWorkforce, onWorkforceChange, mode, dispatch]);
  const assignedUids = useMemo(() => {
    return new Set([
      ...selectedWorkforce.supervisors.map((worker) => String(worker.uid)),
      ...selectedWorkforce.employees.map((worker) => String(worker.uid)),
      ...selectedWorkforce.contractors.map((worker) => String(worker.uid)),
    ]);
  }, [selectedWorkforce]);

  const activeCurrentWf = useMemo(() => {
    const isActiveWorker = (worker) => {
      const assignment = worker?.WorkforcesByJobs ?? worker?.WorkforcesByJobEvents;

      return assignment?.isDeleted !== true;
    };

    return (currentWf ?? []).filter(isActiveWorker);
  }, [currentWf]);

  const getAssignmentRole = (worker) => {
    return (
      worker?.WorkforcesByJobEvents?.jobWorkforceRole ||
      worker?.WorkforcesByJobs?.jobWorkforceRole ||
      worker?.workforceRole ||
      worker?.role ||
      worker?.type ||
      ""
    ).toLowerCase();
  };

  const assignedSupervisors = useMemo(() => {
    return activeCurrentWf.filter((worker) => getAssignmentRole(worker) === "supervisor");
  }, [activeCurrentWf]);

  const assignedEmployees = useMemo(() => {
    return activeCurrentWf.filter((worker) => getAssignmentRole(worker) === "employee");
  }, [activeCurrentWf]);

  const assignedContractors = useMemo(() => {
    return activeCurrentWf.filter((worker) => getAssignmentRole(worker) === "contractor");
  }, [activeCurrentWf]);

  const availableSupervisors = useMemo(() => {
    return supervisor.filter((worker) => !assignedUids.has(String(worker.uid)));
  }, [supervisor, assignedUids]);

  const availableEmployees = useMemo(() => {
    return employee.filter((worker) => !assignedUids.has(String(worker.uid)));
  }, [employee, assignedUids]);

  const availableContractors = useMemo(() => {
    return contractor.filter((worker) => !assignedUids.has(String(worker.uid)));
  }, [contractor, assignedUids]);

  const allWorkers = useMemo(() => {
    return [...supervisor, ...employee, ...contractor, ...activeCurrentWf];
  }, [supervisor, employee, contractor, activeCurrentWf]);

  const getWorkerByItem = (item) => {
    const uid = item.replace("worker-", "");

    return allWorkers.find((worker) => String(worker.uid) === String(uid));
  };

  const getWorkerRole = (worker) => {
    return getAssignmentRole(worker);
  };

  const getAssignedRole = (worker) => {
    return getAssignmentRole(worker);
  };

  const getSlotRole = (slot) => {
    if (slot?.includes("supervisor")) return "supervisor";
    if (slot?.includes("employee")) return "employee";
    if (slot?.includes("contractor")) return "contractor";

    return "";
  };

  const markSwapyItemError = (item, status) => {
    const element = slotsRef.current?.querySelector(`[data-swapy-item="${item}"]`);

    if (!element) return;

    if (status === "error") {
      element.style.border = "3px solid #d32f2f";
      element.style.backgroundColor = "#ffebee";
      element.style.color = "#d32f2f";
      return;
    }

    element.style.border = "";
    element.style.backgroundColor = "";
    element.style.color = "";
  };

  const syncSelectedStaff = (slotItemMap) => {
    if (!slotItemMap?.asArray) return;

    const newOrder = slotItemMap.asArray;
    const lastMap = { ...lastSlotMapRef.current };

    const selectedSlots = newOrder.filter(({ slot, item }) => slot?.startsWith("workforce") && item);

    const employees = [];
    const contractors = [];
    const supervisors = [];

    selectedSlots.forEach(({ slot, item }) => {
      const worker = getWorkerByItem(item);
      const uid = item.replace("worker-", "");

      const itemRole = getWorkerRole(worker);
      const slotRole = getSlotRole(slot);

      const isValid = itemRole && slotRole && itemRole === slotRole;

      if (!isValid || !worker) return;

      const staffMember = {
        ...worker,
        uid,
        type: itemRole,
        slot,
      };

      if (itemRole === "employee") employees.push(staffMember);
      if (itemRole === "contractor") contractors.push(staffMember);
      if (itemRole === "supervisor") supervisors.push(staffMember);
    });

    const changes = newOrder
      .filter(({ slot, item }) => {
        if (!item) return false;

        return lastMap[item] !== slot || itemsWithError.includes(item);
      })

      .map(({ slot, item }) => {
        const worker = getWorkerByItem(item);
        const itemRole = getWorkerRole(worker);
        const slotRole = getSlotRole(slot);
        const isWorkforceSlot = slot?.startsWith("workforce");
        const isValid = !isWorkforceSlot || itemRole === slotRole;

        lastMap[item] = slot;

        return {
          item,
          isValid,
        };
      });

    const hasInvalidDrop = changes.some(({ isValid }) => !isValid);

    changes.forEach(({ item, isValid }) => {
      if (!item) return;

      if (!isValid) {
        markSwapyItemError(item, "error");
      } else {
        markSwapyItemError(item, null);
      }
    });

    const nextErrors = changes.reduce((errors, { item, isValid }) => {
      if (!item) return errors;

      if (!isValid && !errors.includes(item)) {
        return [...errors, item];
      }

      if (isValid) {
        return errors.filter((errorItem) => errorItem !== item);
      }

      return errors;
    }, itemsWithError);

    const cleanErrors = nextErrors.filter((errorItem) => typeof errorItem === "string" && errorItem.trim() !== "");

    setItemsWithError(cleanErrors);

    if (mode === "create") {
      dispatch(setStaffErrors(cleanErrors));
    }

    const successItems = changes.filter(({ item, isValid }) => item && isValid).map(({ item }) => item);

    setItemsWithSuccess(successItems);

    const nextSelectedWorkforce = {
      supervisors,
      employees,
      contractors,
    };

    if (!hasInvalidDrop) {
      lastSlotMapRef.current = lastMap;
      selectedWorkforceRef.current = nextSelectedWorkforce;
    }

    return {
      hasInvalidDrop,
      nextSelectedWorkforce,
    };
  };

  useEffect(() => {
    if (loading) return;
    if (!slotsRef.current) return;
    if (swapyInitializedRef.current) return;

    const timeoutId = setTimeout(() => {
      if (!slotsRef.current) return;
      if (swapyInitializedRef.current) return;

      const isValid = validateSwapyStructure(slotsRef.current);

      if (!isValid) {
        console.error("Swapy was not created because the structure is invalid.");
        return;
      }

      let swapy;

      try {
        swapy = createSwapy(slotsRef.current, {
          animation: "dynamic",
          swapMode: "drop",
          autoScrollOnDrag: true,
        });
      } catch (error) {
        console.error("Swapy create error:", error);
        return;
      }

      swapyRef.current = swapy;
      swapyInitializedRef.current = true;

      const initMap = {};

      slotsRef.current.querySelectorAll("[data-swapy-item]").forEach((el) => {
        const item = el.getAttribute("data-swapy-item");
        const slotEl = el.closest("[data-swapy-slot]");
        const slot = slotEl?.getAttribute("data-swapy-slot") || null;

        if (item) {
          initMap[item] = slot;
        }
      });

      lastSlotMapRef.current = initMap;

      swapy.onSwapEnd((event) => {
        const revisionAtSwap = selectionRevisionRef.current;
        const slotItemMap = event.newSlotItemMap || event.slotItemMap || swapy.slotItemMap?.();

        const result = syncSelectedStaff(slotItemMap);
        if (selectionRevisionRef.current !== revisionAtSwap) return;

        if (result?.hasInvalidDrop) {
          setItemsWithError([]);
          setTimeout(() => {
            if (selectionRevisionRef.current !== revisionAtSwap) return;
            if (swapyRef.current) {
              swapyRef.current.destroy();
              swapyRef.current = null;
            }

            swapyInitializedRef.current = false;
            lastSlotMapRef.current = {};
            setSwapyRenderKey((prev) => prev + 1);
          }, 300);

          return;
        }

        const nextSelectedWorkforce = result.nextSelectedWorkforce;

        if (swapyRef.current) {
          swapyRef.current.destroy();
          swapyRef.current = null;
        }

        swapyInitializedRef.current = false;
        lastSlotMapRef.current = {};

        setSelectedWorkforce(nextSelectedWorkforce);

        if (mode === "create") {
          dispatch(setEmployees(nextSelectedWorkforce.employees));
          dispatch(setContractors(nextSelectedWorkforce.contractors));
          dispatch(setSupervisors(nextSelectedWorkforce.supervisors));
        }

        setSwapyRenderKey((prev) => prev + 1);
      });
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading, mode, swapyRenderKey, selectedWorkforce]);

  const handleChange = (_, newValue) => setValue(newValue);

  const saveJobDefaultWorkforce = async ({ workforceToSave, payload, applyToEvents }) => {
    if (!jobId) {
      showSnackbar("Missing job. Workforce could not be saved.", "error");
      return;
    }

    if (!assignmentVersionRef.current) {
      showSnackbar("Reload the job before changing assignments.", "error");
      return;
    }

    let result;
    try {
      result = await updateJobDefaultWorkforceMutation.mutateAsync({
        jobId,
        workforce: payload,
        applyToEvents,
        expectedJobUpdatedAt: assignmentVersionRef.current,
      });
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message ||
          (error?.response?.status === 409 ? "Assignments changed. Reload the job before saving." : "Workforce could not be saved."),
        "error",
      );
      return;
    }

    const warning = result?.warning || result?.data?.warning;
    const assignmentVersion = result?.assignmentVersion || result?.data?.assignmentVersion;
    if (assignmentVersion) assignmentVersionRef.current = assignmentVersion;

    showSnackbar(
      warning ||
        (applyToEvents ? "Job default workforce updated and applied to existing events." : "Job default workforce updated for future events only."),
      warning ? "warning" : "success",
    );

    await onSaveWorkforce?.(workforceToSave);
  };

  const getExistingEventsCount = () => {
    const backendCount = Number(job?.eventsCount ?? 0);

    if (backendCount > 0) {
      return backendCount;
    }

    const cachedJobEvents = queryClient.getQueryData(["job-events", jobId]);

    if (Array.isArray(cachedJobEvents)) {
      return cachedJobEvents.length;
    }

    if (Array.isArray(cachedJobEvents?.data)) {
      return cachedJobEvents.data.length;
    }

    if (Array.isArray(cachedJobEvents?.data?.data)) {
      return cachedJobEvents.data.data.length;
    }

    if (Array.isArray(job?.events)) {
      return job.events.length;
    }

    return 0;
  };

  const handleSaveWorkforce = async () => {
    if (mode === "create") {
      console.log("Create mode workforce:", selectedWorkforceRef.current);
      return;
    }

    setIsProcessing(true);

    try {
      const workforceToSave = selectedWorkforceRef.current;
      console.log("[JobApproved] workforceToSave", workforceToSave);
      console.log("[JobApproved] saveMode", saveMode);
      const payload = [
        ...workforceToSave.supervisors.map((item) => ({
          workforceUid: item.uid,
          jobWorkforceRole: "supervisor",
        })),
        ...workforceToSave.employees.map((item) => ({
          workforceUid: item.uid,
          jobWorkforceRole: "employee",
        })),
        ...workforceToSave.contractors.map((item) => ({
          workforceUid: item.uid,
          jobWorkforceRole: "contractor",
        })),
      ];

      if (saveMode === "job-default") {
        const hasExistingEvents = getExistingEventsCount() > 0;

        if (!hasExistingEvents) {
          await saveJobDefaultWorkforce({
            workforceToSave,
            payload,
            applyToEvents: false,
          });

          return;
        }

        setIsProcessing(false);

        showDialog({
          type: "confirm",
          title: "Apply workforce changes?",
          subtitle: "This job already has scheduled events",
          message:
            "You updated the default workforce for this job. Do you want to update the scheduled events too, or only use this workforce for new events?",
          confirmLabel: "Update scheduled events",
          cancelLabel: "Only new events",
          onConfirm: async () => {
            setIsProcessing(true);

            try {
              await saveJobDefaultWorkforce({
                workforceToSave,
                payload,
                applyToEvents: true,
              });
            } finally {
              setIsProcessing(false);
            }
          },
          onCancel: async () => {
            setIsProcessing(true);

            try {
              await saveJobDefaultWorkforce({
                workforceToSave,
                payload,
                applyToEvents: false,
              });
            } finally {
              setIsProcessing(false);
            }
          },
        });

        return;
      }

      if (!jobEventUid) {
        showSnackbar("Missing job event. Workforce could not be saved.", "error");
        return;
      }

      if (!assignmentVersionRef.current) {
        showSnackbar("Reload the job before changing assignments.", "error");
        return;
      }

      const result = await updateJobEventWorkforceMutation.mutateAsync({
        jobEventUid,
        workforce: payload,
        expectedJobUpdatedAt: assignmentVersionRef.current,
      });

      const assignmentVersion = result?.assignmentVersion || result?.data?.assignmentVersion;
      if (assignmentVersion) assignmentVersionRef.current = assignmentVersion;

      showSnackbar("Workforce changed successfully!", "success");

      await onSaveWorkforce?.();
    } catch (error) {
      console.error("Error saving workforce:", error);
      showSnackbar(
        error?.response?.data?.message ||
          (error?.response?.status === 409 ? "Assignments changed. Reload the job before saving." : "Workforce could not be saved."),
        "error",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveAssignedStaff = ({ staff, type }) => {
    const normalizedType = normalizeWorkforceType(type);
    const itemId = `worker-${staff.uid}`;

    if (swapyRef.current) {
      swapyRef.current.destroy();
      swapyRef.current = null;
    }

    swapyInitializedRef.current = false;
    lastSlotMapRef.current = {};

    setItemsWithError((prev) => prev.filter((item) => item !== itemId));

    const nextSelectedWorkforce = {
      ...selectedWorkforceRef.current,
      [normalizedType]: (selectedWorkforceRef.current[normalizedType] ?? []).filter((item) => String(item.uid) !== String(staff.uid)),
    };

    selectionRevisionRef.current += 1;
    selectedWorkforceRef.current = nextSelectedWorkforce;
    setSelectedWorkforce(nextSelectedWorkforce);

    if (mode === "create") {
      dispatch(setSupervisors(nextSelectedWorkforce.supervisors));
      dispatch(setEmployees(nextSelectedWorkforce.employees));
      dispatch(setContractors(nextSelectedWorkforce.contractors));
    }

    setSelectedStaff([]);
    setTouchedItems([]);
    setSwapyRenderKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (!["edit", "create"].includes(mode)) return;
    if (initializedFromCurrentWfRef.current) return;
    if (!activeCurrentWf.length) return;

    const initialSelectedWorkforce = {
      supervisors: assignedSupervisors.map((worker) => ({
        ...worker,
        type: "supervisor",
        workforceRole: worker.workforceRole ?? "supervisor",
      })),
      employees: assignedEmployees.map((worker) => ({
        ...worker,
        type: "employee",
        workforceRole: worker.workforceRole ?? "employee",
      })),
      contractors: assignedContractors.map((worker) => ({
        ...worker,
        type: "contractor",
        workforceRole: worker.workforceRole ?? "contractor",
      })),
    };

    setSelectedWorkforce(initialSelectedWorkforce);
    selectedWorkforceRef.current = initialSelectedWorkforce;
    initializedFromCurrentWfRef.current = true;

    if (swapyRef.current) {
      swapyRef.current.destroy();
      swapyRef.current = null;
    }

    swapyInitializedRef.current = false;
    lastSlotMapRef.current = {};
    setSwapyRenderKey((prev) => prev + 1);

    if (mode === "create") {
      dispatch(setSupervisors(initialSelectedWorkforce.supervisors));
      dispatch(setEmployees(initialSelectedWorkforce.employees));
      dispatch(setContractors(initialSelectedWorkforce.contractors));
    }
  }, [mode, activeCurrentWf.length, assignedSupervisors, assignedEmployees, assignedContractors, dispatch]);

  if (mode === "view") {
    const assignedList = activeCurrentWf;

    const viewSupervisors = assignedList.filter((worker) => getAssignedRole(worker) === "supervisor");

    const viewEmployees = assignedList.filter((worker) => getAssignedRole(worker) === "employee");

    const viewContractors = assignedList.filter((worker) => getAssignedRole(worker) === "contractor");

    const renderWorker = (worker) => (
      <Box
        key={worker.uid}
        sx={{
          px: 1.4,
          py: 1,
          borderRadius: "12px",
          border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: worker.reassignment ? "#b91c1c" : colors.gray[100],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.76rem",
            fontWeight: 600,
            color: worker.reassignment ? "#fff" : colors.gray[400],
            flexShrink: 0,
          }}
        >
          {worker.firstName?.charAt(0)}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: worker.reassignment ? "#b91c1c" : colors.gray[700] }}>
            {worker.firstName} {worker.lastName}
          </Typography>
          {worker.reassignment && <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#b91c1c" }}>Must be reassigned</Typography>}
        </Box>
      </Box>
    );

    const renderGroup = (title, workers) => (
      <Box
        sx={{
          flex: 1,
          minWidth: 220,
          border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            px: 1.65,
            py: 1.1,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderBottom: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
          }}
        >
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.gray[700] }}>{title}</Typography>
        </Box>

        <Box
          sx={{
            p: 1.65,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 80,
          }}
        >
          {workers.length > 0 ? (
            workers.map(renderWorker)
          ) : (
            <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>No assigned workers.</Typography>
          )}
        </Box>
      </Box>
    );

    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          width: "100%",
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          boxShadow: "none",
        }}
      >
        <Typography sx={{ textAlign: "center", mb: 2.25, fontSize: "1.08rem", fontWeight: 600, color: colors.gray[800] }}>
          Assigned Workforce
        </Typography>

        {viewSupervisors.concat(viewEmployees, viewContractors).some((worker) => worker.reassignment) && (
          <Box sx={{ mb: 1.5, px: 1.5, py: 1.25, borderRadius: 1.5, backgroundColor: "#fee2e2", border: "1px solid #fecaca" }}>
            <Typography sx={{ color: "#b91c1c", fontWeight: 800, fontSize: "0.86rem" }}>A worker on this visit must be reassigned.</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {renderGroup("Supervisors", viewSupervisors)}
          {renderGroup("Employees", viewEmployees)}
          {renderGroup("Contractors", viewContractors)}
        </Box>
      </Box>
    );
  }

  const editWorkforceModalKey = `Edit-Workforce-${jobEventUid}`;

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {DialogComponent}

      {isProcessing && (
        <Box
          sx={{
            height: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ color: blue[600], mb: 2 }} />
          <Typography variant="h6" sx={{ color: grey[800] }}>
            Saving workforce...
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2, boxShadow: "none" }}>
            <Typography variant="h5" textAlign="center" mb={2}>
              {saveMode === "job-default" ? "Select Job Default Workforce" : "Select Event Workforce"}{" "}
              <Typography component="span" variant="caption" display={userRole === IRoles.Client ? "none" : ""}>
                Required *
              </Typography>
            </Typography>

            {selectedWorkforce.supervisors
              .concat(selectedWorkforce.employees, selectedWorkforce.contractors)
              .some((worker) => worker.reassignment) && (
              <Box sx={{ mb: 2, px: 1.5, py: 1.25, borderRadius: 1.5, backgroundColor: "#fee2e2", border: "1px solid #fecaca" }}>
                <Typography sx={{ color: "#b91c1c", fontWeight: 800, fontSize: "0.86rem" }}>A worker on this visit must be reassigned.</Typography>
                {selectedWorkforce.supervisors
                  .concat(selectedWorkforce.employees, selectedWorkforce.contractors)
                  .filter((worker) => worker.reassignment)
                  .map((worker) => (
                    <Typography key={worker.uid} sx={{ color: "#b91c1c", fontSize: "0.78rem", mt: 0.4 }}>
                      {worker.firstName} {worker.lastName}: {worker.reassignment.reason}
                    </Typography>
                  ))}
              </Box>
            )}

            {itemsWithError.length > 0 && (
              <Fade in={showStaffErrorBanner} timeout={500} unmountOnExit>
                <Box>
                  <Banner
                    description="Some workers are assigned to the wrong role. Move them to the correct column before saving."
                    icon={<ErrorOutlineOutlined fontSize="inherit" />}
                    onDismiss={() => setShowStaffErrorBanner(false)}
                    title="Error"
                    variant="error"
                  />
                </Box>
              </Fade>
            )}

            <Box
              key={swapyRenderKey}
              ref={slotsRef}
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", md: "row" },
                gap: 2,
                mt: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  width: "100%",
                }}
              >
                <SwapyTeamSection
                  title="Supervisors"
                  maxSlots={MAX_SLOTS_SUPERVISOR}
                  type="supervisors"
                  assignedStaff={selectedWorkforce.supervisors}
                  onRemoveStaff={handleRemoveAssignedStaff}
                  itemsWithSuccess={itemsWithSuccess}
                />

                <SwapyTeamSection
                  title="Employees"
                  maxSlots={MAX_SLOTS_EMPLOYEE}
                  type="employees"
                  assignedStaff={selectedWorkforce.employees}
                  onRemoveStaff={handleRemoveAssignedStaff}
                  itemsWithSuccess={itemsWithSuccess}
                />

                <SwapyTeamSection
                  title="Contractors"
                  maxSlots={MAX_SLOTS_CONTRACTOR}
                  type="contractors"
                  assignedStaff={selectedWorkforce.contractors}
                  onRemoveStaff={handleRemoveAssignedStaff}
                  itemsWithSuccess={itemsWithSuccess}
                />
              </Box>

              <Box sx={{ flex: 1, border: "1px solid var(--fx-surface-border, #E2E8F0)", borderRadius: 2 }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="job-approved tabs"
                  sx={{ backgroundColor: "var(--fx-surface-muted, #F1F5F9)", borderStartStartRadius: 2, borderStartEndRadius: 2 }}
                >
                  <Tab sx={{ fontSize: 12, width: 80 }} label="Supervisor" {...a11yProps(0)} />
                  <Tab sx={{ fontSize: 12, width: 80 }} label="Employee" {...a11yProps(1)} />
                  <Tab sx={{ fontSize: 12, width: 80 }} label="Contractor" {...a11yProps(2)} />
                </Tabs>

                <Box sx={{ p: 2, display: value === 0 ? "block" : "none" }}>
                  <Typography variant="h6" fontSize={16} mb={1}>
                    Supervisor Team
                  </Typography>

                  <SwapyContainerTeam role={availableSupervisors} type="supervisor" />
                </Box>

                <Box sx={{ p: 2, display: value === 1 ? "block" : "none" }}>
                  <Typography variant="h6" fontSize={16} mb={1}>
                    Employee Team
                  </Typography>

                  <SwapyContainerTeam role={availableEmployees} type="employee" />
                </Box>

                <Box sx={{ p: 2, display: value === 2 ? "block" : "none" }}>
                  <Typography variant="h6" fontSize={16} mb={1}>
                    Contractor Team
                  </Typography>

                  <SwapyContainerTeam role={availableContractors} type="contractor" />
                </Box>
              </Box>
            </Box>

            {mode === "edit" && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
                }}
              >
                <Button
                  onClick={handleSaveWorkforce}
                  disabled={isProcessing || itemsWithError.length > 0}
                  sx={{
                    minWidth: 170,
                    height: 42,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    bgcolor: colors.orange.base,
                    color: "white",

                    "&:hover": {
                      bgcolor: colors.orange[600],
                      boxShadow: "none",
                    },

                    "&.Mui-disabled": {
                      bgcolor: grey[300],
                      color: grey[600],
                      boxShadow: "none",
                    },
                  }}
                >
                  {isProcessing ? "Saving..." : saveMode === "job-default" ? "Save Job Workforce" : "Save Event Workforce"}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {userRole === IRoles.Employee && <JobEmployeeAccept data={job} />}
    </Box>
  );
}
